import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { aalignAPI, marketplaceAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Zap, 
  Globe, 
  Building2, 
  Users, 
  Calendar, 
  Target, 
  DollarSign,
  Briefcase,
  EyeOff,
  CheckCircle,
  Clock,
  FileText,
  Tag,
  ChevronRight,
  Send,
  Loader2,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

function AalignCampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, isConnectedWith, isPendingWith, addPendingRequest } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAlreadyServing, setIsAlreadyServing] = useState(false);
  
  // Business Request Dialog State
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestSubject, setRequestSubject] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  
  // Booking Dialog State
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingLeadInputs, setBookingLeadInputs] = useState({}); // { lead_type: target_count }
  const [bookingEndDate, setBookingEndDate] = useState('');
  const [sendingBooking, setSendingBooking] = useState(false);

  useEffect(function() {
    // Wait for auth to finish loading and user to be available
    if (!authLoading && user) {
      loadCampaign();
    }
  }, [id, user, authLoading]);

  // Check if seller is already serving this campaign
  useEffect(function() {
    if (user && user.role === 'seller' && id) {
      marketplaceAPI.getMyBookings()
        .then(function(response) {
          if (response.data.success) {
            const bookings = response.data.data || [];
            const isServing = bookings.some(function(b) {
              const bookedId = (b.cmp_id || '').replace('cmp_', '');
              return bookedId === id;
            });
            setIsAlreadyServing(isServing);
          }
        })
        .catch(function(err) {
          console.error('Failed to check booked campaigns:', err);
        });
    }
  }, [user, id]);

  // Format Buyer ID: First initial of first word + First initial of last word + first 2 chars + last 4 chars (uppercase)
  function formatBuyerId(buyerId, name) {
    if (!buyerId) return 'N/A';
    
    let initials = '';
    if (name) {
      const words = name.trim().split(/\s+/);
      if (words.length >= 1) {
        initials += words[0].charAt(0).toUpperCase();
      }
      if (words.length >= 2) {
        initials += words[words.length - 1].charAt(0).toUpperCase();
      } else if (words.length === 1) {
        initials += words[0].charAt(1) ? words[0].charAt(1).toUpperCase() : '';
      }
    }
    
    const first2 = buyerId.substring(0, 2).toUpperCase();
    const last4 = buyerId.slice(-4).toUpperCase();
    
    return initials + first2 + last4;
  }

  function loadCampaign() {
    setLoading(true);
    
    // Use different API based on user role - buyers use marketplace API, sellers use aalign API
    const isBuyerUser = user && user.role === 'buyer';
    const apiCall = isBuyerUser 
      ? marketplaceAPI.getMyListingDetail(id)
      : aalignAPI.getCampaignDetail(id);
    
    apiCall.then(function(response) {
      if (response.data.success) {
        const data = response.data.data;
        data.id = id;
        setCampaign(data);
      } else {
        toast.error('Failed to load campaign');
        navigate(isBuyerUser ? '/my-listings' : '/marketplace');
      }
    }).catch(function(error) {
      console.error('Failed to load campaign:', error);
      toast.error('Failed to load campaign');
      navigate(isBuyerUser ? '/my-listings' : '/marketplace');
    }).finally(function() {
      setLoading(false);
    });
  }

  function maskCampaignName(name) {
    if (!name) return '••••••••';
    const words = name.replace(/_/g, ' ').split(' ');
    if (words.length <= 2) {
      const first = words[0].substring(0, 3) + '•••';
      const second = words[1] ? ' ' + words[1].substring(0, 2) + '•••' : '';
      return first + second;
    }
    return words[0] + ' ' + words[1].substring(0, 2) + '••• •••';
  }

  function formatCampaignId(campaignId) {
    if (!campaignId || campaignId.length < 6) return campaignId || 'N/A';
    return campaignId.substring(0, 2) + 'xxx' + campaignId.slice(-4);
  }

  function formatBudget(budget) {
    if (!budget) return 'N/A';
    const num = parseFloat(budget);
    if (isNaN(num)) return budget;
    return '$' + num.toLocaleString();
  }

  function calculateTotalBudget(offerings) {
    if (!offerings || offerings.length === 0) return null;
    let total = 0;
    for (let i = 0; i < offerings.length; i++) {
      const price = parseFloat(offerings[i].price) || 0;
      const target = parseInt(offerings[i].target) || 0;
      total += price * target;
    }
    return total;
  }

  function renderBadgeList(items, className) {
    const result = [];
    for (let i = 0; i < items.length; i++) {
      result.push(
        <Badge key={i} variant="secondary" className={className}>
          {items[i]}
        </Badge>
      );
    }
    return result;
  }

  function renderOutlineBadgeList(items) {
    const result = [];
    for (let i = 0; i < items.length; i++) {
      result.push(
        <Badge key={i} variant="outline">{items[i]}</Badge>
      );
    }
    return result;
  }

  function renderGeoWiseTargets(targets) {
    const result = [];
    for (let i = 0; i < targets.length; i++) {
      const geoTarget = targets[i];
      result.push(
        <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
          <span className="text-sm font-medium text-slate-700">{geoTarget.geo}</span>
          <div className="text-right">
            <span className="text-sm font-semibold text-slate-900">{geoTarget.target} leads</span>
            <Badge variant="outline" className="ml-2 text-xs">{geoTarget.status}</Badge>
          </div>
        </div>
      );
    }
    return result;
  }

  function renderAssets(assets) {
    const result = [];
    for (let i = 0; i < assets.length; i++) {
      const asset = assets[i];
      result.push(
        <div key={i} className="bg-slate-50 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-medium text-slate-900">{asset.asset_name}</div>
              <Badge variant="outline" className="mt-1">{asset.asset_type}</Badge>
            </div>
          </div>
          {asset.remarks && (
            <p className="text-sm text-slate-600 mt-2">{asset.remarks}</p>
          )}
        </div>
      );
    }
    return result;
  }

  function renderCustomQuestions(questions) {
    const result = [];
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const answers = [];
      for (let j = 0; j < q.answers.length; j++) {
        answers.push(
          <Badge key={j} variant="outline" className="bg-white">{q.answers[j]}</Badge>
        );
      }
      result.push(
        <div key={i} className="bg-slate-50 rounded-lg p-4">
          <div className="font-medium text-slate-900 mb-2">{q.question}</div>
          <div className="flex flex-wrap gap-2">{answers}</div>
        </div>
      );
    }
    return result;
  }

  function renderOfferings(offerings, calculatedBudget) {
    const result = [];
    for (let i = 0; i < offerings.length; i++) {
      const offering = offerings[i];
      result.push(
        <div key={i} className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-emerald-700 text-lg">{offering.lead_type}</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-slate-500">Price per Lead</div>
              <div className="font-bold text-slate-900">${offering.price}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Target</div>
              <div className="font-bold text-slate-900">{offering.target} leads</div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {result}
        <Separator className="my-4" />
        <div className="flex justify-between items-center">
          <span className="font-medium text-slate-700">Total Budget</span>
          <span className="font-bold text-xl text-slate-900">
            {calculatedBudget ? formatBudget(calculatedBudget) : 'N/A'}
          </span>
        </div>
      </div>
    );
  }

  function renderDeliveryDays(days) {
    const result = [];
    for (let i = 0; i < days.length; i++) {
      result.push(
        <Badge key={i} variant="secondary">{days[i]}</Badge>
      );
    }
    return result;
  }

  // Business Request Handlers
  function openRequestDialog() {
    setRequestSubject('Lead Generation Partnership Request');
    setRequestMessage('Hi, I am interested in providing leads for your campaign. I have experience in the relevant industries and regions.');
    setRequestDialogOpen(true);
  }

  function closeRequestDialog() {
    setRequestDialogOpen(false);
    setRequestSubject('');
    setRequestMessage('');
  }

  function handleSendRequest() {
    if (!campaign || !requestSubject || !requestMessage) {
      toast.error('Please fill in all fields');
      return;
    }

    const buyerId = campaign.buyer_id || campaign.account_id;
    
    setSendingRequest(true);
    marketplaceAPI.sendRequest({
      to_user_id: buyerId,
      to_role: 'buyer',
      subject: requestSubject,
      message: requestMessage
    })
      .then(function(response) {
        if (response.data.status === 'success' || response.data.success) {
          toast.success('Business request sent successfully! Waiting for buyer approval.');
          addPendingRequest(buyerId);
          closeRequestDialog();
        } else {
          toast.error(response.data.message || 'Failed to send request');
        }
      })
      .catch(function(err) {
        console.error('Failed to send request:', err);
        toast.error(err.response?.data?.detail || 'Failed to send request');
      })
      .finally(function() {
        setSendingRequest(false);
      });
  }

  // Booking Handlers
  function openBookingDialog() {
    setBookingLeadInputs({});
    setBookingEndDate('');
    setBookingDialogOpen(true);
  }

  function closeBookingDialog() {
    setBookingDialogOpen(false);
    setBookingLeadInputs({});
    setBookingEndDate('');
  }
  
  function updateLeadInput(leadType, value) {
    setBookingLeadInputs(function(prev) {
      return { ...prev, [leadType]: value };
    });
  }

  function renderOfferingsRows() {
    if (!campaign) return null;
    // Support both 'offering' (new API) and 'offerings' (old API)
    const offerings = campaign.offering || campaign.offerings || [];
    if (offerings.length === 0) return null;
    
    const rows = [];
    for (let idx = 0; idx < offerings.length; idx++) {
      const offering = offerings[idx];
      // Support both 'cpl' (new API) and 'price' (old API)
      const cpl = offering.cpl || offering.price || 0;
      // Support 'deficit' (new API) or calculate from target - delivered
      const remaining = offering.deficit !== undefined ? offering.deficit : ((offering.target || 0) - (offering.delivered || 0));
      
      rows.push(
        <tr key={idx} className="hover:bg-slate-50">
          <td className="px-3 py-2 font-medium text-slate-900">{offering.lead_type}</td>
          <td className="px-3 py-2 text-right text-slate-600">${cpl}</td>
          <td className="px-3 py-2 text-right text-slate-600">{offering.target || 0}</td>
          <td className="px-3 py-2 text-right text-emerald-600 font-medium">{remaining}</td>
          <td className="px-3 py-2">
            <Input
              type="number"
              min="0"
              max={remaining}
              placeholder="0"
              className="w-20 h-8 text-center"
              value={bookingLeadInputs[offering.lead_type] || ''}
              onChange={function(e) { updateLeadInput(offering.lead_type, e.target.value); }}
              data-testid={'booking-input-' + offering.lead_type}
            />
          </td>
        </tr>
      );
    }
    return rows;
  }

  function handleBookCampaign() {
    if (!campaign || !bookingEndDate) {
      toast.error('Please select your delivery end date');
      return;
    }
    
    // Get campaign end date - required for the API
    const campaignEndDate = campaign.end_date || '';
    if (!campaignEndDate) {
      toast.error('Campaign end date not available.');
      return;
    }
    
    // Validate that delivery date is not after campaign end date
    if (new Date(bookingEndDate) > new Date(campaignEndDate)) {
      toast.error('Your delivery date cannot be after the campaign end date (' + campaignEndDate + ')');
      return;
    }
    
    // Support both 'offering' (new API) and 'offerings' (old API)
    const offerings = campaign.offering || campaign.offerings || [];
    const bookings = [];
    
    for (let i = 0; i < offerings.length; i++) {
      const offering = offerings[i];
      const leadType = offering.lead_type;
      const inputValue = bookingLeadInputs[leadType];
      
      if (inputValue && parseInt(inputValue) > 0) {
        const leadsCommit = parseInt(inputValue);
        // Support 'deficit' (new API) or calculate from target - delivered
        const maxTarget = offering.deficit !== undefined ? offering.deficit : ((offering.target || 0) - (offering.delivered || 0));
        
        if (leadsCommit > maxTarget) {
          toast.error(leadType + ': Cannot exceed remaining target of ' + maxTarget + ' leads');
          return;
        }
        
        // Support both 'cpl' (new API) and 'price' (old API)
        const cpl = offering.cpl || offering.price || 0;
        
        bookings.push({
          account_id: campaign.account_id || campaign.buyer_id,
          partner_id: user?.id,
          cmp_id: campaign.id,
          lead_type: leadType,
          target: offering.target || 0,
          cpl: parseFloat(cpl),
          end_date: campaignEndDate,
          leads_commit: leadsCommit,
          end_date_commit: bookingEndDate
        });
      }
    }
    
    if (bookings.length === 0) {
      toast.error('Please enter the number of leads for at least one lead type');
      return;
    }

    setSendingBooking(true);
    const bookingPayload = {
      bookings: bookings
    };

    marketplaceAPI.bookCampaign(bookingPayload)
      .then(function(response) {
        if (response.data.status === 'success' || response.data.success) {
          toast.success('Campaign booked successfully!');
          closeBookingDialog();
        } else {
          toast.error(response.data.message || 'Failed to book campaign');
        }
      })
      .catch(function(err) {
        console.error('Failed to book campaign:', err);
        toast.error(err.response?.data?.detail || 'Failed to book campaign');
      })
      .finally(function() {
        setSendingBooking(false);
      });
  }

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full mb-4" />
          <Skeleton className="h-48 w-full" />
        </div>
      </Layout>
    );
  }

  if (!campaign) return null;

  const specs = campaign.specifications || {};
  const geoList = specs.geo || [];
  const industries = specs.industry_types || [];
  const jobTitles = specs.job_title_keywords || '';
  const leadTypes = specs.lead_types || [];
  const offerings = campaign.offerings || [];
  const employeeSizes = specs.employee_size || [];
  const revenueSizes = specs.revenue_size || [];
  const jobFunctions = specs.job_functions || [];
  const jobLevels = specs.job_levels || [];
  const deliveryDays = campaign.delivery_days || [];
  const assets = campaign.assets || [];
  const customQuestions = campaign.custom_questions || [];
  const geoWiseTargets = campaign.geo_wise_targets || [];
  const calculatedBudget = calculateTotalBudget(offerings);
  const maskedName = maskCampaignName(campaign.campaign_name);
  const statusClass = campaign.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600';

  return (
    <Layout>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="aalign-campaign-detail-page">
        <Button variant="ghost" className="mb-6" onClick={function() { navigate(user && user.role === 'buyer' ? '/my-listings' : '/marketplace'); }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {user && user.role === 'buyer' ? 'Back to My Campaigns' : 'Back to Marketplace'}
        </Button>

        <div className="flex flex-col lg:flex-row gap-8 mb-8">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                <Zap className="w-3 h-3 mr-1" />
                AALIGN Campaign
              </Badge>
              {isAlreadyServing && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Already Serving
                </Badge>
              )}
              <Badge className={statusClass}>{campaign.status || 'Unknown'}</Badge>
              {campaign.type && (
                <Badge variant="outline" className="bg-orange-50 text-orange-700">
                  <Briefcase className="w-3 h-3 mr-1" />
                  {campaign.type}
                </Badge>
              )}
            </div>
            <h1 className="text-3xl font-bold text-slate-400 mb-2 flex items-center gap-3">
              <EyeOff className="w-7 h-7" />
              {maskedName}
            </h1>
            <div className="flex items-center gap-4 mb-4">
              <p className="text-sm text-slate-400 font-mono">BID: {formatBuyerId(campaign.account_id || campaign.buyer_id, campaign.buyer_name)}</p>
              <p className="text-sm text-slate-400 font-mono">CID: {formatCampaignId(id)}</p>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center text-sm text-slate-600">
                <Target className="w-4 h-4 mr-2 text-orange-500" />
                <span className="font-semibold">{campaign.total_target || 'N/A'}</span>&nbsp;leads target
              </div>
              <div className="flex items-center text-sm text-slate-600">
                <DollarSign className="w-4 h-4 mr-2 text-orange-500" />
                <span className="font-semibold">{calculatedBudget ? formatBudget(calculatedBudget) : 'N/A'}</span>&nbsp;total budget
              </div>
              {campaign.end_date && (
                <div className="flex items-center text-sm text-slate-600">
                  <Calendar className="w-4 h-4 mr-2 text-orange-500" />
                  Ends&nbsp;<span className="font-semibold">{campaign.end_date}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Globe className="w-5 h-5 text-slate-500" />
                  Geographic Targeting
                </CardTitle>
              </CardHeader>
              <CardContent>
                {geoList.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {renderBadgeList(geoList, 'bg-blue-50 text-blue-700 border-blue-200')}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No geographic targeting specified</p>
                )}

                {geoWiseTargets.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="text-sm font-medium text-slate-700 mb-3">Region-wise Targets</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {renderGeoWiseTargets(geoWiseTargets)}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-500" />
                  Target Industries
                </CardTitle>
              </CardHeader>
              <CardContent>
                {industries.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {renderBadgeList(industries, 'bg-amber-50 text-amber-700 border-amber-200')}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No industries specified</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-slate-500" />
                  Target Job Titles
                </CardTitle>
              </CardHeader>
              <CardContent>
                {jobTitles ? (
                  <p className="text-slate-700 leading-relaxed">{jobTitles}</p>
                ) : (
                  <p className="text-slate-500 text-sm">No job titles specified</p>
                )}

                {(jobFunctions.length > 0 || jobLevels.length > 0) && (
                  <div className="mt-4 pt-4 border-t space-y-4">
                    {jobFunctions.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-2">Job Functions</div>
                        <div className="flex flex-wrap gap-2">
                          {renderOutlineBadgeList(jobFunctions)}
                        </div>
                      </div>
                    )}
                    {jobLevels.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-2">Job Levels</div>
                        <div className="flex flex-wrap gap-2">
                          {renderOutlineBadgeList(jobLevels)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {(employeeSizes.length > 0 || revenueSizes.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-slate-500" />
                    Company Targeting
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {employeeSizes.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-2">Employee Size</div>
                      <div className="flex flex-wrap gap-2">
                        {renderBadgeList(employeeSizes, 'bg-slate-100')}
                      </div>
                    </div>
                  )}
                  {revenueSizes.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-2">Revenue Size</div>
                      <div className="flex flex-wrap gap-2">
                        {renderBadgeList(revenueSizes, 'bg-slate-100')}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Tag className="w-5 h-5 text-slate-500" />
                  Lead Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leadTypes.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {renderBadgeList(leadTypes, 'bg-purple-100 text-purple-700 border-purple-200 text-sm px-3 py-1')}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No lead types specified</p>
                )}
              </CardContent>
            </Card>

            {assets.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-500" />
                    Campaign Assets
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">{renderAssets(assets)}</div>
                </CardContent>
              </Card>
            )}

            {customQuestions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-slate-500" />
                    Custom Questions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">{renderCustomQuestions(customQuestions)}</div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              
              <Card className="shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-slate-500" />
                    Offerings & Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {offerings.length > 0 ? renderOfferings(offerings, calculatedBudget) : (
                    <p className="text-slate-500 text-sm">No offerings available</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-slate-500" />
                    Delivery Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {campaign.end_date && (
                    <div className="mb-4">
                      <div className="text-sm text-slate-500">Campaign End Date</div>
                      <div className="font-semibold text-slate-900">{campaign.end_date}</div>
                    </div>
                  )}
                  {deliveryDays.length > 0 && (
                    <div>
                      <div className="text-sm text-slate-500 mb-2">Delivery Days</div>
                      <div className="flex flex-wrap gap-2">{renderDeliveryDays(deliveryDays)}</div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Conditional Action Button - Only show for sellers */}
              {user && user.role === 'seller' && (function() {
                const buyerId = campaign.buyer_id || campaign.account_id;
                const isConnected = buyerId ? isConnectedWith(buyerId) : false;
                const isPending = buyerId ? isPendingWith(buyerId) : false;
                
                if (isConnected) {
                  return (
                    <>
                      <Button 
                        className="w-full bg-orange-500 hover:bg-orange-600" 
                        size="lg"
                        onClick={openBookingDialog}
                        data-testid="book-campaign-btn"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Book Campaign
                      </Button>
                      <p className="text-xs text-green-600 text-center flex items-center justify-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        You are connected with this buyer
                      </p>
                    </>
                  );
                } else if (isPending) {
                  return (
                    <>
                      <Button 
                        className="w-full bg-amber-100 text-amber-700 border-amber-200 cursor-not-allowed" 
                        size="lg"
                        disabled
                        data-testid="pending-request-btn"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Pending Request
                      </Button>
                      <p className="text-xs text-amber-600 text-center">
                        Waiting for buyer approval
                      </p>
                    </>
                  );
                } else {
                  return (
                    <>
                      <Button 
                        className="w-full bg-orange-500 hover:bg-orange-600" 
                        size="lg"
                        onClick={openRequestDialog}
                        data-testid="send-request-btn"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Send Business Request
                      </Button>
                      <p className="text-xs text-slate-500 text-center">
                        Request connection to book this campaign
                      </p>
                    </>
                  );
                }
              })()}
            </div>
          </div>
        </div>

        {campaign.remarks && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-lg">Campaign Notes & Remarks</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">{campaign.remarks}</p>
            </CardContent>
          </Card>
        )}

        {/* Business Request Dialog */}
        <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Send Business Request</DialogTitle>
              <DialogDescription>
                Send a connection request to the campaign buyer to start providing leads.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="request-subject">Subject</Label>
                <Input
                  id="request-subject"
                  value={requestSubject}
                  onChange={function(e) { setRequestSubject(e.target.value); }}
                  placeholder="Enter subject"
                  data-testid="request-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="request-message">Message</Label>
                <Textarea
                  id="request-message"
                  value={requestMessage}
                  onChange={function(e) { setRequestMessage(e.target.value); }}
                  placeholder="Introduce yourself and your lead generation capabilities..."
                  rows={4}
                  data-testid="request-message-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeRequestDialog} disabled={sendingRequest}>
                Cancel
              </Button>
              <Button onClick={handleSendRequest} disabled={sendingRequest} data-testid="confirm-send-request-btn">
                {sendingRequest ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Booking Dialog */}
        <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
          <DialogContent className="sm:max-w-[550px]">
            <DialogHeader>
              <DialogTitle>Book Campaign</DialogTitle>
              <DialogDescription>
                Enter the number of leads you want to deliver for each lead type.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Campaign End Date */}
              {campaign && (
                <div className="bg-orange-50 rounded-lg p-3 flex items-center justify-between">
                  <div className="text-sm text-slate-600">Campaign End Date</div>
                  <div className="font-semibold text-slate-900">
                    {campaign.end_date 
                      ? new Date(campaign.end_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })
                      : 'Not specified'}
                  </div>
                </div>
              )}

              {/* Lead Types Table */}
              {campaign && (campaign.offering || campaign.offerings) && (campaign.offering || campaign.offerings).length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-700">Lead Type</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-700">CPL</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-700">Target</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-700">Remaining</th>
                        <th className="px-3 py-2 text-center font-medium text-slate-700">Your Commit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {renderOfferingsRows()}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-slate-500">
                  No lead types available
                </div>
              )}

              {/* Delivery End Date */}
              <div className="space-y-2">
                <Label htmlFor="booking-end-date">Your Committed Delivery Date</Label>
                <Input
                  id="booking-end-date"
                  type="date"
                  value={bookingEndDate}
                  max={campaign?.end_date || ''}
                  onChange={function(e) { setBookingEndDate(e.target.value); }}
                  data-testid="booking-end-date-input"
                />
                <p className="text-xs text-slate-500">
                  {campaign?.end_date 
                    ? 'Must be on or before campaign end date (' + campaign.end_date + ')'
                    : 'When you commit to deliver the leads by'}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeBookingDialog} disabled={sendingBooking}>
                Cancel
              </Button>
              <Button onClick={handleBookCampaign} disabled={sendingBooking} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-booking-btn">
                {sendingBooking ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Booking...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Confirm Booking
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default AalignCampaignDetail;
